import { cn } from '@lensora/ui'
import { Check, ChevronDown, Plus } from 'lucide-react'
import * as React from 'react'
import { Link } from 'react-router-dom'

import { FaviconImg } from './FaviconImg'

import { ACTIVE_WEBSITE_STORAGE_KEY, useActiveWebsite } from '@/hooks/use-active-website'

function extractDomain(url: string): string {
  return (
    url
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0] ?? ''
  )
}

export function WebsiteSwitcher() {
  const { website: activeWebsite, websites: list } = useActiveWebsite()
  const [open, setOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  function handleSwitch(id: string) {
    if (id === activeWebsite?.id) { setOpen(false); return }
    localStorage.setItem(ACTIVE_WEBSITE_STORAGE_KEY, id)
    setOpen(false)
    window.location.reload()
  }

  if (!activeWebsite) return null

  const domain = extractDomain(activeWebsite.url)
  const displayName = activeWebsite.name ?? activeWebsite.url

  // Single website — no dropdown
  if (list.length === 1) {
    return (
      <div className="flex items-center gap-2.5 rounded-lg border border-border bg-surface-raised px-3 py-2">
        <FaviconImg domain={domain} name={displayName} size={22} className="rounded" />
        <span className="flex-1 truncate text-sm font-medium text-text">{displayName}</span>
      </div>
    )
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2.5 rounded-lg border border-border bg-surface-raised px-3 py-2 text-left transition-colors hover:bg-surface-overlay"
      >
        <FaviconImg domain={domain} name={displayName} size={22} className="rounded" />
        <span className="flex-1 truncate text-sm font-medium text-text">{displayName}</span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-text-muted transition-transform duration-150',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1.5 w-64 overflow-hidden rounded-xl border border-border bg-surface shadow-lg">
          <p className="px-3 pb-1 pt-2.5 text-[10px] font-semibold uppercase tracking-widest text-text-muted">
            Your Websites
          </p>
          <div className="p-1.5">
            {list.map((site) => {
              const isActive = site.id === activeWebsite.id
              const siteDomain = extractDomain(site.url)
              const siteName = site.name ?? site.url
              return (
                <button
                  key={site.id}
                  onClick={() => handleSwitch(site.id)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors',
                    isActive ? 'bg-brand-accent/10' : 'hover:bg-surface-raised'
                  )}
                >
                  <FaviconImg domain={siteDomain} name={siteName} size={28} className="rounded-md" />
                  <div className="min-w-0 flex-1">
                    <p className={cn('truncate text-sm font-medium', isActive ? 'text-text' : 'text-text')}>
                      {siteName}
                    </p>
                    <p className="truncate text-xs text-text-muted">{site.url}</p>
                  </div>
                  {isActive && <Check className="h-3.5 w-3.5 shrink-0 text-brand-accent" />}
                </button>
              )
            })}
          </div>
          <div className="border-t border-border p-1.5">
            <Link
              to="/dashboard/settings/websites"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-text-muted transition-colors hover:bg-surface-raised hover:text-text"
            >
              <Plus className="h-3.5 w-3.5" />
              Add website
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
